import { useContext } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";

// From its own module rather than the contexts barrel: that barrel also pulls
// in Onboarding, which reaches back into this side of the app.
import { UserContext } from "../contexts/User";

export const CART_ITEMS_KEY = "cartItems";
export const CART_ORDER_KEY = "cartOrder";

export function useCartItems(orderId) {
  // CartProvider wraps the WHOLE app, sign-in included, so without this guard
  // the cart was fetched for a logged-out visitor: GET
  // /api/order-management/cart answered 401, and the error notifier told
  // someone sitting on the sign-in page that their session had expired.
  // Onboarding already gates its fetch on user?.id; this is that, for the cart.
  const { user } = useContext(UserContext) ?? {};

  const query = useQuery(
    [CART_ITEMS_KEY],
    async () => {
      if (orderId) {
        // If orderId is provided, return empty array here because the items will
        // be fetched with the order details in useCartOrder
        return [];
      }

      const response = await axios.get(
        "/api/order-management/cart" + (orderId ? `/${orderId}` : "")
      );

      return response.data;
    },
    { enabled: !!user?.id }
  );

  return {
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    items: query.data || [],
  };
}

export function useCartOrder(orderId) {
  const query = useQuery([CART_ORDER_KEY], async () => {
    const response = await axios.get(
      "/api/order-management/cart-order" + (orderId ? `/${orderId}` : "")
    );

    return response.data;
  });

  return {
    isLoadingOrder: query.isLoading,
    order: query.data || {},
  };
}

export function useCheckoutOrder(orderId) {
  const { items, isLoading: isLoadingItems } = useCartItems(orderId);
  const { order, isLoadingOrder } = useCartOrder(orderId);

  const isLoading = isLoadingItems || isLoadingOrder;

  if (isLoading) {
    return {
      isLoading,
      items: [],
      order: {},
    };
  }

  return {
    isLoading,
    items: orderId ? order.items : items,
    order,
  };
}

export function useAddToCart() {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (data) => {
      // Optional `requestConfig` passes through to axios so callers
      // can opt into metadata.silent when they intend to surface a
      // more specific 422 message themselves (see
      // createNewUnpaidOrder in useOrderManagement). Existing callers
      // pass no requestConfig — they keep the global generic toast.
      const response = await axios.post(
        "/api/order-management/cart",
        {
          items: data.items,
          client_id: data.clientId,
          is_registering: data.isRegistering,
        },
        data.requestConfig
      );

      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(CART_ITEMS_KEY);
    },
  });

  return {
    addToCart: mutation.mutateAsync,
    isAddingToCart: mutation.isLoading,
  };
}

export function useRemoveOrderItem(invalidateCartItems = true) {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (orderItemId) => {
      const response = await axios.delete(
        `/api/order-management/order-items/${orderItemId}`
      );

      return response.data;
    },
    onSuccess: () => {
      if (invalidateCartItems) {
        queryClient.invalidateQueries(CART_ITEMS_KEY);
      }
    },
  });

  return {
    removeOrderItem: mutation.mutateAsync,
    isRemovingOrderItem: mutation.isLoading,
  };
}

export function useRemovePayment() {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async ({ paymentId, refundConfirmed }) => {
      // `metadata.silent` opts this DELETE out of the global axios
      // interceptor's toast/rethrow handling so the confirmation modal owns
      // ALL error messaging — including the 502 "Xero reversal failed"
      // message, which the interceptor otherwise swallows (>=500 returns
      // without rethrowing, so the caller could never read it). The reject
      // now carries the original error with `response.data.message` intact.
      const response = await axios.delete(
        `/api/order-management/payments/${paymentId}`,
        { data: { refund_confirmed: refundConfirmed }, metadata: { silent: true } }
      );

      return response.data;
    },
    onSuccess: () => {
      // Refresh the queries that drive the cart / order + PX summary panels
      // so removed money disappears without a manual Save. The order-edit
      // flow additionally re-derives its in-memory totals from local state
      // (see the row-cleanup in Payments.jsx) since it holds `order` in a
      // constate store rather than react-query.
      queryClient.invalidateQueries(CART_ITEMS_KEY);
      queryClient.invalidateQueries(CART_ORDER_KEY);
    },
  });

  return {
    removePayment: mutation.mutateAsync,
    isRemovingPayment: mutation.isLoading,
  };
}

export function usePayDeposits() {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (data) => {
      const response = await axios.post("/api/order-management/deposit", data);

      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(CART_ITEMS_KEY);
      queryClient.invalidateQueries(CART_ORDER_KEY);
    },
  });

  return {
    payDeposits: mutation.mutateAsync,
    isPayingDeposits: mutation.isLoading,
  };
}

export function usePayOrder() {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (data) => {
      const response = await axios.post("/api/order-management/pay", data);

      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(CART_ITEMS_KEY);
      queryClient.invalidateQueries(CART_ORDER_KEY);
    },
  });

  return {
    payOrder: mutation.mutateAsync,
    isPayingOrder: mutation.isLoading,
  };
}

export function useCreatePxOrder() {
  const mutation = useMutation({
    mutationFn: async (data) => {
      const response = await axios.post("/api/order-management/px-order", data);

      return response.data;
    },
  });

  return {
    createPxOrder: mutation.mutateAsync,
    isCreatingPxOrder: mutation.isLoading,
  };
}

export function useRegisterPxOrder() {
  const mutation = useMutation({
    mutationFn: async ({ requestConfig, ...data }) => {
      const response = await axios.post(
        "/api/order-management/register-px-order",
        data,
        requestConfig
      );

      return response.data;
    },
  });

  return {
    registerPxOrder: mutation.mutateAsync,
    isRegisteringPxOrder: mutation.isLoading,
  };
}

export function useRegisterDeposits() {
  const mutation = useMutation({
    mutationFn: async (data) => {
      const response = await axios.post(
        "/api/order-management/register-deposits",
        data
      );

      return response.data;
    },
  });

  return {
    registerDeposits: mutation.mutateAsync,
    isRegisteringDeposits: mutation.isLoading,
  };
}

export function useRegisterPaidOrder() {
  const mutation = useMutation({
    mutationFn: async (data) => {
      const response = await axios.post(
        "/api/order-management/register-paid-order",
        data
      );

      return response.data;
    },
  });

  return {
    registerPaidOrder: mutation.mutateAsync,
    isRegisteringPaidOrder: mutation.isLoading,
  };
}

export function useRegisterCommissions() {
  const mutation = useMutation({
    mutationFn: async (data) => {
      const response = await axios.post(
        "/api/order-management/register-commissions",
        data
      );

      return response.data;
    },
  });

  return {
    registerCommissions: mutation.mutateAsync,
    isRegisteringCommissions: mutation.isLoading,
  };
}
